import { Injectable } from '@angular/core';
import { params } from '@app/configurations';
import { ApiService } from '@app/core/api/api.service';
import { ApiCommonRoutes, ApiEndpoint } from '@app/core/models/api.models';
import { GlobalFilterType } from '@app/core/models/common.models';
import { SubFiltersService } from '@app/core/services/subfilters.service';
import { CustomerState } from '@app/core/store/states/customer/customer.state';
import { GlobalFilterNotFound } from '@app/core/store/states/global-filters/global-filters-state.actions';
import { GlobalFiltersState } from '@app/core/store/states/global-filters/global-filters.state';
import { RouterNavState } from '@app/core/store/states/router-nav/router-nav.state';
import { encodeSearchTerms } from '@app/core/utilities/url.utilities';
import { ForcedFilter } from '@app/shared/components';
import { defaultToastError } from '@app/shared/operators/default-toast-error.operator';
import { TranslateService } from '@ngx-translate/core';
import { Store } from '@ngxs/store';
import { catchError, map, Observable, of, switchMap, tap } from 'rxjs';
import { ListItem } from '../models/selector.model';
import { BaseSelectorService } from './base-selector.service';
import { AdGroupsDTO, ConvertCase, getAdGroupFilters } from './global-filters.utils';

@Injectable()
export class AdGroupSelectorService extends BaseSelectorService {
  requiredParams = [
    params.GLOBAL_FILTER_AD_GROUPS_ID,
    params.GLOBAL_FILTER_AD_GROUPS_QUERY,
    params.GLOBAL_FILTER_DEST_AD_GROUPS_ID,
    params.GLOBAL_FILTER_DEST_AD_GROUPS_QUERY,
  ];

  includedData: any = [
    'campaignName',
    'campaignType',
    'adspertAdgroupId',
    'adgroupStatus',
    'adgroupName',
    'accountPlatform',
    'accountName',
    'accountId',
  ];
  defaultLabel = 'global-filter.adgroup-selector.placeholder';
  defaultSubfiltersType = 'adGroupsDefaultFilter';
  dto = AdGroupsDTO;

  readonly srcSelectedItem$ = this.getSelectedItemStream(params.GLOBAL_FILTER_AD_GROUPS_ID);
  readonly destSelectedItem$ = this.getSelectedItemStream(params.GLOBAL_FILTER_DEST_AD_GROUPS_ID);

  readonly subfilterOptions = () => this.subFiltersService.getAdGroupsSubFilters();

  getSelectedItem(id): Observable<ListItem> {
    if (this.cache.items?.[id]) return of(this.cache.items[id]);
    this.loader$.next(true);

    return this.store.select(CustomerState.customerId).pipe(
      switchMap((customerId) =>
        this.apiService
          .getObject({
            endpoint: ApiEndpoint.Customers,
            include: this.includedData,
            route: [customerId, ApiCommonRoutes.AdGroups, id],
          })
          .pipe(
            map(({ data }: any) => {
              this.loader$.next(false);
              this.cache.items[data.adspertAdgroupId] = this.dto(data);
              return this.cache.items[data.adspertAdgroupId];
            }),
            defaultToastError(
              'Failed to fetch selected Ad Groups in the Global Filter. Please try with another selection.',
            ),
            catchError((err) => {
              this.store.dispatch(new GlobalFilterNotFound(GlobalFilterType.ADGROUP));
              this.loader$.next(false);
              return of({});
            }),
          ),
      ),
    );
  }

  getItems(
    { query, filters = [] },
    offset = 0,
    forDestination: boolean,
    forcedFilters?: ForcedFilter,
  ): Observable<{ items: ListItem[]; count: number }> {
    this.listLoader$.next(true);
    const cq = forDestination ? params.GLOBAL_FILTER_DEST_CAMPAIGN_QUERY : params.GLOBAL_FILTER_CAMPAIGN_QUERY;
    const state = this.store.selectSnapshot(RouterNavState.queryParams);

    const temp =
      state[cq] !== undefined
        ? state[cq]
        : this.store.selectSnapshot(GlobalFiltersState.filtersValues).campaignsDefaultFilter;
    const tmpFilter =
      this.subFiltersService.getFiltersForAPI(temp, this.subFiltersService.getCampaignSubFilters()) ?? [];

    const refiners: any = {
      sortValues: { adgroupStatus: 1, adgroupName: 2 },
      filterValues: {
        ...getAdGroupFilters(
          [...filters, ...tmpFilter].filter(([k]) => k.startsWith('filter_')),
          this.store.selectSnapshot(GlobalFiltersState.filtersValues),
          state,
          forDestination,
        ),
        ...this.getForcedFilters(forcedFilters, forDestination),
      },
    };

    if (!!query) {
      refiners.searchFields =
        'performanceGroupName,performanceGroupId,amzPortfolioId,amzPortfolioName,campaignId,campaignName,adgroupId,adgroupName';
      refiners.searchTerm = encodeSearchTerms(query);
    }

    const customerId = this.store.selectSnapshot(CustomerState.customerId);
    return this.apiService
      .getList({
        endpoint: ApiEndpoint.Customers,
        route: [customerId, ApiCommonRoutes.AdGroups],
        include: this.includedData,
        ...refiners,
        searchValues: this.getSearchValues(filters),
        limit: this.limit,
        offset: offset,
      })
      .pipe(
        tap(() => this.listLoader$.next(false)),
        map(({ data, meta }) => {
          const items = data.map(this.dto);
          return { items, count: meta.pagination.total };
        }),
        defaultToastError('Failed to fetch Ad Groups. Please try with another selection.'),
        catchError((err) => {
          this.listLoader$.next(false);
          return of({ items: [], count: 0 });
        }),
      );
  }

  constructor(
    translate: TranslateService,
    store: Store,
    apiService: ApiService,
    private subFiltersService: SubFiltersService,
  ) {
    super(translate, store, apiService);
  }
}

function newFunction(filters: any[]) {
  const searchValues = ConvertCase(
    filters.filter(([k]) => k.startsWith('search_')),
    'search_',
  );

  if (Object.keys(searchValues)?.length)
    Object.keys(searchValues).forEach((d) => (searchValues[d] = encodeURIComponent(searchValues[d])));
  return searchValues;
}
